import {
  BROADCAST_TYPE_ICONS,
  BROADCAST_TYPE_ICON_ALT,
} from "src/assets/broadcasts";
import { historyItemToFullPayload } from "src/lib/broadcastService";
import { NotificationHistoryListItem } from "src/types/broadcasts";
import { Copy, MoreVertical, Pencil, Trash2 } from "lucide-react";
import CopyButton from "../CopyButton";
import { useEffect, useRef, useState } from "react";
import Collapsible from "../Collapsible";
import { createPortal } from "react-dom";

// --- Sub-Components ---

/**
 * Row-level action menu.
 *
 * Edit and Duplicate are mutually exclusive by row status:
 *   - draft     → Edit (resume composing) + Delete
 *   - published → Duplicate (compose a new copy) + Delete
 *
 * The parent decides which by passing exactly one of `onEdit` / `onDuplicate`.
 * Passing both would imply a row that is "both a draft and not", which is a bug.
 */
const RowMenu: React.FC<{
  onDelete: () => void;
  onDuplicate?: () => void;
  onEdit?: () => void;
}> = ({ onDelete, onDuplicate, onEdit }) => {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const updateMenuPosition = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    setMenuPosition({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  };

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!open) {
      updateMenuPosition();
      setOpen(true);
      return;
    }

    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;

    const close = (e: MouseEvent) => {
      const target = e.target as Node;

      if (
        buttonRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }

      setOpen(false);
    };

    const reposition = () => updateMenuPosition();

    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);

    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleMenu}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Broadcast actions"
        className={[
          "rounded-xl border-2 border-transparent p-1.5 text-[#777777]",
          "transition-all hover:border-[#E5E5E5] hover:bg-[#F7F7F7] hover:text-[#1F1F1F]",
          "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#84D8FF]/50",
          "opacity-0 group-hover/broadcast-row:opacity-100",
          open ? "opacity-100 border-[#E5E5E5] bg-[#F7F7F7]" : "",
        ].join(" ")}
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              top: menuPosition.top,
              right: menuPosition.right,
            }}
            className={[
              "fixed z-[9999] w-44 overflow-hidden rounded-2xl border-2 border-[#E5E5E5]",
              "bg-white shadow-[0_6px_0_#D0D0D0]",
            ].join(" ")}
            onClick={(e) => e.stopPropagation()}
          >
            {onEdit && (
              <button
                type="button"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onEdit();
                }}
                className="flex w-full items-center gap-2 border-b border-[#E5E5E5] px-3 py-2 text-sm font-extrabold text-amber-800 hover:bg-amber-50"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </button>
            )}

            {onDuplicate && (
              <button
                type="button"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onDuplicate();
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm font-extrabold text-[#1F1F1F] hover:bg-[#F4F4F5]"
              >
                <Copy className="h-4 w-4 text-[#6B7280]" />
                Duplicate
              </button>
            )}

            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onDelete();
              }}
              className="flex w-full items-center gap-2 border-t border-[#E5E5E5] px-3 py-2 text-sm font-extrabold text-rose-600 hover:bg-rose-50"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>,
          document.body,
        )}
    </>
  );
};

const tsToString = (ts: NotificationHistoryListItem["createdAt"]): string => {
  try {
    return ts.toDate().toLocaleString();
  } catch {
    return "—";
  }
};

const BroadcastItem: React.FC<{
  item: NotificationHistoryListItem;
  isExpanded: boolean;
  onToggle: () => void;
  canManage: boolean;
  onDuplicate: (item: NotificationHistoryListItem) => void;
  onEdit?: (item: NotificationHistoryListItem) => void;
  onDelete: (item: NotificationHistoryListItem) => void;
}> = ({
  item,
  isExpanded,
  onToggle,
  canManage,
  onDuplicate,
  onEdit,
  onDelete,
}) => {
  const fullPayload = historyItemToFullPayload(item);
  const fullPayloadJson = JSON.stringify(fullPayload, null, 2);
  const isDraft = item.status === "draft";
  // Drafts → Edit only. Published → Duplicate only. Never both — they're
  // mutually exclusive actions, so the unused handler is `undefined` and
  // both the kebab menu and the expanded footer hide the disabled affordance.
  const handleEdit = isDraft && onEdit ? () => onEdit(item) : undefined;
  const handleDuplicate = !isDraft ? () => onDuplicate(item) : undefined;
  const header = (
    <div className="flex items-center gap-2 px-3 py-2">
      <button
        onClick={onToggle}
        className="flex flex-1 items-center gap-2 text-left min-w-0"
      >
        <span className="flex shrink-0 items-center justify-center rounded-lg  text-white">
          <img
            src={BROADCAST_TYPE_ICONS[item.type]}
            alt={BROADCAST_TYPE_ICON_ALT[item.type]}
            className="h-8 w-8 object-contain"
          />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p
              className={`truncate text-sm font-bold ${isDraft ? "text-[#92400E]" : "text-[#1F1F1F]"}`}
            >
              {item.title || (isDraft ? "(untitled draft)" : item.title)}
            </p>
            {isDraft && (
              <span className="shrink-0 rounded-md border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-amber-800">
                Draft
              </span>
            )}
            {/* environment */}
            <span
              className={`hidden shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase sm:inline-block ${item.env === "prod" ? "bg-rose-100 text-rose-700 border-rose-200" : "bg-indigo-100 text-indigo-700 border-indigo-200"}`}
            >
              {item.env}
            </span>
          </div>
          <p className="truncate text-xs text-[#6B7280]">
            {item.body || (isDraft ? "Empty body — still editable." : "")}
          </p>
        </div>
      </button>
    </div>
  );
  const trailing = (
    <div className="flex items-center justify-between gap-3">
      <div className="hidden shrink-0 text-right text-[11px] text-[#9CA3AF] sm:block">
        <p className="truncate">{item.actor.displayName ?? item.actor.email}</p>
        <p>{tsToString(item.createdAt)}</p>
      </div>

      {canManage && (
        <div className="shrink-0">
          <RowMenu
            onDelete={() => onDelete(item)}
            onDuplicate={handleDuplicate}
            onEdit={handleEdit}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="group/broadcast-row relative">
      <Collapsible
        className="shadow-none"
        header={header}
        trailing={trailing}
        leadingChevron={true}
        open={isExpanded}
        onOpenChange={onToggle}
      >
        <div className="border-t border-[#EEEEEE] bg-[#FAFAFA] px-4 py-4 text-xs">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#777777]">
              API request payload
            </p>
            <CopyButton value={fullPayloadJson} />
          </div>
          <pre className="w-full max-w-full overflow-x-auto whitespace-pre-wrap break-all rounded-b-[1.25rem] bg-[#0F172A] p-4 font-mono text-xs leading-relaxed text-emerald-100">
            {fullPayloadJson}
          </pre>
          <div className="mt-3 flex justify-end gap-2">
            {canManage && (
              <>
                {handleEdit && (
                  <button
                    onClick={handleEdit}
                    className="inline-flex items-center gap-1.5 rounded-lg border-2 border-amber-500 bg-amber-50 px-3 py-1.5 text-xs font-extrabold uppercase text-amber-800 shadow-[0_2px_0_#FDE68A] active:translate-y-0.5"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit draft
                  </button>
                )}
                {handleDuplicate && (
                  <button
                    onClick={handleDuplicate}
                    className="inline-flex items-center gap-1.5 rounded-lg border-2 border-indigo-500 bg-indigo-50 px-3 py-1.5 text-xs font-extrabold uppercase text-indigo-700 shadow-[0_2px_0_#C7D2FE] active:translate-y-0.5"
                  >
                    <Copy className="h-3.5 w-3.5" /> Duplicate
                  </button>
                )}
                <button
                  onClick={() => onDelete(item)}
                  className="inline-flex items-center gap-1.5 rounded-lg border-2 border-rose-500 bg-rose-50 px-3 py-1.5 text-xs font-extrabold uppercase text-rose-700 shadow-[0_2px_0_#FECDD3] active:translate-y-0.5"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </>
            )}
          </div>
        </div>
      </Collapsible>
    </div>
  );
};

export default BroadcastItem;
